// Procurement page — Phase 3 implementation.
//
// Auditor-grade procurement event log under 2 CFR 200.318–.327:
//   - Events tab: live table of every RFP/IFB/quote/sole-source event,
//     with threshold-tier-aware compliance flags (formal procurements
//     missing IGE / cost analysis / MBE solicitation get red flags).
//   - New-event drawer: inline form, threshold tier auto-classifies as
//     you type the estimated amount.
//   - Obligations tab: catalog-driven obligation rows for procurement
//     (FED-FIN-PROC, FED-FIN-DBE, FED-FIN-COI, FED-FIN-LOBBY).
//
// Backend:
//   GET  /api/bead-pm/projects/:id/procurement-events
//   POST /api/bead-pm/projects/:id/procurement-events
//   PUT  /api/bead-pm/projects/:id/procurement-events/:eventId
//   DELETE /api/bead-pm/projects/:id/procurement-events/:eventId
//   GET  /api/bead-pm/projects/:id/obligations?category=federal_financial
//   GET  /api/bead/vendors

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import DocumentUploader from '../components/DocumentUploader';

type Project = { id: string; project_name: string; project_code: string };
type Vendor = { id: string; name: string; uei: string | null };

type ProcurementEvent = {
  id: string;
  project_id: string;
  event_code: string;
  procurement_type: 'micro_purchase' | 'simplified' | 'sealed_bid' | 'rfp' | 'sole_source';
  threshold_tier: 'micro' | 'simplified' | 'formal';
  description: string;
  estimated_amount_usd: string | number;
  igce_amount_usd: string | number | null;
  igce_document_id: string | null;
  cost_analysis_document_id: string | null;
  conflict_disclosure_document_id: string | null;
  vendors_solicited: Array<{ name: string; mbe?: boolean; wbe?: boolean; veteran?: boolean }>;
  bids_received_count: number | null;
  awarded_vendor_id: string | null;
  awarded_vendor_name: string | null;
  award_amount_usd: string | number | null;
  award_date: string | null;
  contract_type: string | null;
  status: 'planned' | 'soliciting' | 'evaluating' | 'awarded' | 'cancelled';
  notes: string | null;
  created_at: string;
};

type Obligation = {
  id: string; code: string; name: string; cadence_rule: string;
  source_cite: string | null; due_date: string;
  status: 'pending' | 'in_progress' | 'submitted' | 'accepted' | 'rejected' | 'overdue' | 'na';
};

const PROCUREMENT_OBLIGATION_CODES = new Set([
  'FED-FIN-PROC', 'FED-FIN-DBE', 'FED-FIN-COI', 'FED-FIN-LOBBY',
]);

const STATUS_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  pending:     { bg: '#f1f5f9', fg: '#475569', label: 'Pending' },
  in_progress: { bg: '#fef3c7', fg: '#92400e', label: 'In progress' },
  submitted:   { bg: '#dbeafe', fg: '#1e40af', label: 'Submitted' },
  accepted:    { bg: '#d1fae5', fg: '#065f46', label: 'Accepted' },
  rejected:    { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
  overdue:     { bg: '#fecaca', fg: '#7f1d1d', label: 'Overdue' },
  na:          { bg: '#e5e7eb', fg: '#6b7280', label: 'N/A' },
  planned:     { bg: '#f1f5f9', fg: '#475569', label: 'Planned' },
  soliciting:  { bg: '#dbeafe', fg: '#1e40af', label: 'Soliciting' },
  evaluating:  { bg: '#fef3c7', fg: '#92400e', label: 'Evaluating' },
  awarded:     { bg: '#d1fae5', fg: '#065f46', label: 'Awarded' },
  cancelled:   { bg: '#fee2e2', fg: '#991b1b', label: 'Cancelled' },
};

const TIER_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  micro:      { bg: '#d1fae5', fg: '#065f46', label: 'Micro' },
  simplified: { bg: '#dbeafe', fg: '#1e40af', label: 'Simplified' },
  formal:     { bg: '#fee2e2', fg: '#991b1b', label: 'Formal' },
};

const TYPE_LABEL: Record<string, string> = {
  micro_purchase: 'Micro-purchase',
  simplified:     'Simplified quote',
  sealed_bid:     'Sealed bid (IFB)',
  rfp:            'Request for proposals (RFP)',
  sole_source:    'Sole-source justification',
};

function deriveTier(amount: number): 'micro' | 'simplified' | 'formal' {
  if (amount <= 10000) return 'micro';
  if (amount <= 250000) return 'simplified';
  return 'formal';
}

function fmtUSD(n: number | string | null | undefined): string {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (!isFinite(v)) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}k`;
  return `$${Math.round(v).toLocaleString()}`;
}

function Badge({ status, map }: { status: string; map: typeof STATUS_BADGE }) {
  const s = map[status] ?? STATUS_BADGE.pending;
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: '2px 8px',
      borderRadius: 999, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}

function StatCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--border, #e2e8f0)',
      borderTop: `3px solid ${color ?? '#3b82f6'}`,
      borderRadius: 6, padding: '12px 14px',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '.06em', color: 'var(--text-muted, #64748b)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted, #64748b)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

const TIER_RULES: Record<'micro' | 'simplified' | 'formal', { label: string; rule: string; required: string[] }> = {
  micro: {
    label: '< $10,000 — micro-purchase',
    rule: 'No competitive quotes required if price is reasonable.',
    required: ['Reasonableness basis (notes)'],
  },
  simplified: {
    label: '$10k–$250k — simplified acquisition',
    rule: '≥ 2 quotes from qualified sources.',
    required: ['Bids received ≥ 2', 'Selection rationale (notes)'],
  },
  formal: {
    label: '> $250k — formal procurement',
    rule: 'Sealed bids OR RFP with public notice.',
    required: ['IGE document', 'Cost analysis', 'MBE/WBE/veteran solicitation log', 'COI disclosure'],
  },
};

/**
 * For each event, return what's MISSING vs the tier requirements.
 * Empty array = compliant.
 */
function complianceGaps(e: ProcurementEvent): string[] {
  const gaps: string[] = [];
  if (e.threshold_tier === 'formal') {
    if (!e.igce_amount_usd && !e.igce_document_id) gaps.push('IGE');
    if (!e.cost_analysis_document_id)              gaps.push('Cost analysis');
    const hasDbe = (e.vendors_solicited ?? []).some(v => v.mbe || v.wbe || v.veteran);
    if (!hasDbe)                                    gaps.push('MBE/WBE solicitation');
    if (!e.conflict_disclosure_document_id)         gaps.push('COI disclosure');
  }
  if (e.threshold_tier === 'simplified') {
    if (!e.bids_received_count || e.bids_received_count < 2) gaps.push('< 2 bids');
  }
  return gaps;
}

export default function ProcurementPage() {
  const qc = useQueryClient();
  const [projectId, setProjectId] = useState<string | null>(null);
  const [tab, setTab] = useState<'events' | 'obligations'>('events');
  const [showNew, setShowNew] = useState(false);
  const [openEvent, setOpenEvent] = useState<string | null>(null);

  const { data: projects } = useQuery<Project[]>({
    queryKey: ['bead-projects'],
    queryFn: () => api.get('/bead/projects'),
  });
  const activeProjectId = projectId ?? projects?.[0]?.id ?? null;

  const { data: vendors = [] } = useQuery<Vendor[]>({
    queryKey: ['bead-vendors-list'],
    queryFn: () => api.get('/bead/vendors'),
  });

  const { data: eventsData } = useQuery<{ items: ProcurementEvent[] }>({
    queryKey: ['procurement-events', activeProjectId],
    queryFn: () => api.get(`/bead-pm/projects/${activeProjectId}/procurement-events`),
    enabled: !!activeProjectId,
  });
  const events: ProcurementEvent[] = eventsData?.items ?? [];

  const { data: financialObligations = [] } = useQuery<Obligation[]>({
    queryKey: ['fin-obligations', activeProjectId],
    queryFn: () => api.get(`/bead-pm/projects/${activeProjectId}/obligations?category=federal_financial`),
    enabled: !!activeProjectId,
  });

  const procurementObligations = useMemo(
    () => financialObligations.filter(o => PROCUREMENT_OBLIGATION_CODES.has(o.code)),
    [financialObligations]
  );

  const createEvent = useMutation({
    mutationFn: (body: any) => api.post(`/bead-pm/projects/${activeProjectId}/procurement-events`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['procurement-events', activeProjectId] });
      setShowNew(false);
    },
  });

  // Keystone: turn an awarded procurement into a linked PO in one click (vendor + amount + event link carried).
  const genPO = useMutation({
    mutationFn: (eventId: string) => api.post(`/finance/pos/from-procurement/${eventId}`, {}),
    onSuccess: (po: any) => {
      qc.invalidateQueries({ queryKey: ['procurement-events', activeProjectId] });
      window.alert('Created ' + (po?.po_number ?? 'a PO') + ' from this award - vendor and amount carried, and it is linked to this procurement. Open Purchase Orders to add line items and receive.');
    },
  });

  const kpis = useMemo(() => {
    const total = events.length;
    const flagged = events.filter(e => complianceGaps(e).length > 0).length;
    const awarded = events.filter(e => e.status === 'awarded').length;
    const totalAwarded = events
      .filter(e => e.status === 'awarded')
      .reduce((acc, e) => acc + Number(e.award_amount_usd ?? 0), 0);
    return { total, flagged, awarded, totalAwarded };
  }, [events]);

  return (
    <div>
      <div className="page__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 className="page__title" style={{ margin: 0, fontSize: 24 }}>Procurement</h1>
          <div className="page__subtitle" style={{ color: 'var(--text-muted, #64748b)', fontSize: 13 }}>
            RFPs, sealed bids, quotes, sole-source — full audit trail under 2 CFR 200.318–.327
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            value={activeProjectId ?? ''}
            onChange={(e) => setProjectId(e.target.value || null)}
            style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border, #e2e8f0)', borderRadius: 6 }}
          >
            {(projects ?? []).map(p => (
              <option key={p.id} value={p.id}>{p.project_name} ({p.project_code})</option>
            ))}
          </select>
          {tab === 'events' && (
            <button
              onClick={() => setShowNew(s => !s)}
              style={{
                padding: '6px 14px', background: '#0f172a', color: '#fff',
                border: '1px solid #0f172a', borderRadius: 4, fontSize: 12,
                fontWeight: 600, cursor: 'pointer',
              }}
            >{showNew ? '× Cancel' : '+ New event'}</button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <StatCard label="Total events"      value={kpis.total} color="#3b82f6" />
        <StatCard label="Compliance flags"  value={kpis.flagged} sub={kpis.flagged > 0 ? 'See red badges below' : 'All clean'} color={kpis.flagged > 0 ? '#ef4444' : '#10b981'} />
        <StatCard label="Awarded"           value={kpis.awarded} color="#10b981" />
        <StatCard label="Total awarded"     value={fmtUSD(kpis.totalAwarded)} color="#0f172a" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border, #e2e8f0)', marginBottom: 16 }}>
        {[
          { id: 'events',      label: 'Events' },
          { id: 'obligations', label: 'Obligations' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id as any)}
            style={{
              padding: '8px 14px', border: 'none', background: 'transparent',
              borderBottom: tab === t.id ? '2px solid #0f172a' : '2px solid transparent',
              color: tab === t.id ? '#0f172a' : '#64748b',
              fontWeight: tab === t.id ? 600 : 500, fontSize: 13, cursor: 'pointer', marginBottom: -1,
            }}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'events' && (
        <>
          {showNew && activeProjectId && (
            <NewEventForm
              vendors={vendors}
              onCancel={() => setShowNew(false)}
              onSubmit={(body) => createEvent.mutate(body)}
              isSubmitting={createEvent.isPending}
            />
          )}

          {/* Events table */}
          <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden', marginBottom: 24 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
                <tr>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Code</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Type</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Tier</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Description</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Estimated</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Award</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Bids</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Status</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Compliance</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: 24, color: '#94a3b8', textAlign: 'center' }}>
                    No procurement events yet. Click <b>+ New event</b> to log one.
                  </td></tr>
                ) : events.map(e => {
                  const gaps = complianceGaps(e);
                  return (
                    <tr key={e.id} onClick={() => setOpenEvent(e.id)} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
                      <td style={{ padding: '8px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#0f172a', fontWeight: 600 }}>{e.event_code}</td>
                      <td style={{ padding: '8px 10px', fontSize: 12 }}>{TYPE_LABEL[e.procurement_type] ?? e.procurement_type}</td>
                      <td style={{ padding: '8px 10px' }}><Badge status={e.threshold_tier} map={TIER_BADGE} /></td>
                      <td style={{ padding: '8px 10px', fontSize: 12, maxWidth: 280 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.description}>{e.description}</div>
                        {e.awarded_vendor_name && (
                          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>→ {e.awarded_vendor_name}</div>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(e.estimated_amount_usd)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(e.award_amount_usd)}</td>
                      <td style={{ padding: '8px 10px', fontSize: 12, color: '#64748b' }}>{e.bids_received_count ?? '—'}</td>
                      <td style={{ padding: '8px 10px' }}><Badge status={e.status} map={STATUS_BADGE} />{e.status === 'awarded' && <button onClick={(ev) => { ev.stopPropagation(); genPO.mutate(e.id); }} disabled={genPO.isPending} title="Create a purchase order from this award" style={{ display: 'block', marginTop: 4, padding: '2px 8px', background: '#0369a1', color: '#fff', border: 'none', borderRadius: 5, fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>Generate PO</button>}</td>
                      <td style={{ padding: '8px 10px' }}>
                        {gaps.length === 0 ? (
                          <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>Clean</span>
                        ) : (
                          <span title={gaps.join(' · ')} style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
                            ⚠ {gaps.length} missing
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Threshold rules card */}
          <h2 style={{ fontSize: 14, marginBottom: 8, color: '#64748b' }}>Threshold rules (2 CFR 200.320)</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {(['micro', 'simplified', 'formal'] as const).map(tier => {
              const r = TIER_RULES[tier];
              const tb = TIER_BADGE[tier];
              return (
                <div key={tier} style={{
                  background: '#fff', border: '1px solid var(--border, #e2e8f0)',
                  borderTop: `3px solid ${tb.fg}`,
                  borderRadius: 6, padding: '12px 14px',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{r.label}</div>
                  <div style={{ fontSize: 12, color: '#0f172a', marginTop: 6 }}>{r.rule}</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>Required artifacts:</div>
                  <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 11, color: '#64748b' }}>
                    {r.required.map(x => <li key={x}>{x}</li>)}
                  </ul>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === 'obligations' && (
        <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
              <tr>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Code</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Requirement</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Authority</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Cadence</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Due</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {procurementObligations.map(o => (
                <tr key={o.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#64748b' }}>{o.code}</td>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{o.name}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.source_cite ?? '—'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{(o.cadence_rule ?? '').replace(/_/g, ' ')}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.due_date ?? '—'}</td>
                  <td style={{ padding: '8px 10px' }}><Badge status={o.status} map={STATUS_BADGE} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openEvent && <EventDetail projectId={activeProjectId!} eventId={openEvent} vendors={vendors} onClose={() => setOpenEvent(null)} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// New-event drawer
// -----------------------------------------------------------------------------

function NewEventForm({
  vendors, onCancel, onSubmit, isSubmitting,
}: {
  vendors: Vendor[];
  onCancel: () => void;
  onSubmit: (body: any) => void;
  isSubmitting: boolean;
}) {
  const [eventCode, setEventCode] = useState('');
  const [type, setType] = useState<'rfp' | 'sealed_bid' | 'simplified' | 'micro_purchase' | 'sole_source'>('rfp');
  const [description, setDescription] = useState('');
  const [estimated, setEstimated] = useState('');
  const [igce, setIgce] = useState('');
  const [bids, setBids] = useState('');
  const [status, setStatus] = useState<'planned' | 'soliciting' | 'evaluating' | 'awarded' | 'cancelled'>('planned');
  const [contractType, setContractType] = useState<string>('firm_fixed_price');
  const [awardedVendor, setAwardedVendor] = useState('');
  const [awardAmount, setAwardAmount] = useState('');
  const [awardDate, setAwardDate] = useState('');
  const [vendorsList, setVendorsList] = useState('');  // newline-separated
  const [includeMbe, setIncludeMbe] = useState(false);
  const [notes, setNotes] = useState('');
  const [igceDocId, setIgceDocId] = useState<string | null>(null);
  const [costAnalysisDocId, setCostAnalysisDocId] = useState<string | null>(null);
  const [coiDocId, setCoiDocId] = useState<string | null>(null);
  const [contractDocId, setContractDocId] = useState<string | null>(null);

  const estNum = Number(estimated) || 0;
  const tier = deriveTier(estNum);
  const tb = TIER_BADGE[tier];

  function handleSubmit() {
    const vlist = vendorsList.split('\n').map(s => s.trim()).filter(Boolean);
    const vobjs = vlist.map((name, i) => ({
      name,
      // Mark first vendor as MBE/WBE if checkbox is on (placeholder UX —
      // real flow will be checkboxes per row).
      ...(includeMbe && i === 0 ? { mbe: true } : {}),
    }));
    onSubmit({
      event_code: eventCode,
      procurement_type: type,
      description,
      estimated_amount_usd: estNum,
      igce_amount_usd: igce ? Number(igce) : null,
      bids_received_count: bids ? Number(bids) : null,
      awarded_vendor_id: awardedVendor || null,
      award_amount_usd: awardAmount ? Number(awardAmount) : null,
      award_date: awardDate || null,
      contract_type: contractType,
      status,
      vendors_solicited: vobjs,
      notes: notes || null,
      igce_document_id: igceDocId,
      cost_analysis_document_id: costAnalysisDocId,
      conflict_disclosure_document_id: coiDocId,
      contract_document_id: contractDocId,
    });
  }

  const inp: React.CSSProperties = {
    width: '100%', padding: '6px 10px', fontSize: 12,
    border: '1px solid var(--border, #e2e8f0)', borderRadius: 4,
    fontFamily: 'inherit', background: '#fff',
  };
  const lbl: React.CSSProperties = {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '.06em', color: '#64748b', marginBottom: 4, display: 'block',
  };

  return (
    <div style={{
      background: '#f8fafc', border: '1px solid var(--border, #e2e8f0)',
      borderRadius: 6, padding: 16, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>New procurement event</h3>
        <span style={{
          background: tb.bg, color: tb.fg, padding: '4px 12px', borderRadius: 999,
          fontSize: 11, fontWeight: 700,
        }}>{tb.label} tier — auto-classified</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={lbl}>Event code</label>
          <input style={inp} value={eventCode} onChange={e => setEventCode(e.target.value)} placeholder="RFP-2026-XX" />
        </div>
        <div>
          <label style={lbl}>Type</label>
          <select style={inp} value={type} onChange={e => setType(e.target.value as any)}>
            <option value="rfp">RFP</option>
            <option value="sealed_bid">Sealed bid (IFB)</option>
            <option value="simplified">Simplified quote</option>
            <option value="micro_purchase">Micro-purchase</option>
            <option value="sole_source">Sole-source</option>
          </select>
        </div>
        <div>
          <label style={lbl}>Estimated amount ($)</label>
          <input style={inp} type="number" value={estimated} onChange={e => setEstimated(e.target.value)} placeholder="0" />
        </div>
        <div>
          <label style={lbl}>{'IGE ($) — required > $250k'}</label>
          <input style={inp} type="number" value={igce} onChange={e => setIgce(e.target.value)} placeholder="—" />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={lbl}>Description</label>
        <input style={inp} value={description} onChange={e => setDescription(e.target.value)} placeholder="What's being procured?" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={lbl}>Vendors solicited (one per line)</label>
          <textarea style={{ ...inp, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
            value={vendorsList} onChange={e => setVendorsList(e.target.value)}
            placeholder={'Acme Corp\nPrime Construction\nTribal Net LLC'} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 11, color: '#64748b' }}>
            <input type="checkbox" checked={includeMbe} onChange={e => setIncludeMbe(e.target.checked)} />
            First vendor is MBE / WBE / veteran-owned
          </label>
        </div>
        <div>
          <label style={lbl}>Notes (incl. cost-reasonableness basis)</label>
          <textarea style={{ ...inp, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Selection rationale, COI disclosed, etc." />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={lbl}>Bids received</label>
          <input style={inp} type="number" value={bids} onChange={e => setBids(e.target.value)} placeholder="—" />
        </div>
        <div>
          <label style={lbl}>Awarded vendor</label>
          <select style={inp} value={awardedVendor} onChange={e => setAwardedVendor(e.target.value)}>
            <option value="">— none —</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Award amount ($)</label>
          <input style={inp} type="number" value={awardAmount} onChange={e => setAwardAmount(e.target.value)} placeholder="—" />
        </div>
        <div>
          <label style={lbl}>Award date</label>
          <input style={inp} type="date" value={awardDate} onChange={e => setAwardDate(e.target.value)} />
        </div>
        <div>
          <label style={lbl}>Contract type</label>
          <select style={inp} value={contractType} onChange={e => setContractType(e.target.value)}>
            <option value="firm_fixed_price">Firm fixed price</option>
            <option value="cost_reimbursable">Cost-reimbursable</option>
            <option value="time_and_materials">Time & materials</option>
            <option value="iqc">IQC / IDIQ</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 12 }}>
        <DocumentUploader value={igceDocId}
          onChange={setIgceDocId}
          docType="igce" label="Independent cost estimate (IGE) PDF"
          hint="Required for procurements > $250k." />
        <DocumentUploader value={costAnalysisDocId}
          onChange={setCostAnalysisDocId}
          docType="cost_analysis" label="Cost or price analysis"
          hint="Required for any procurement over the simplified threshold." />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 12 }}>
        <DocumentUploader value={coiDocId}
          onChange={setCoiDocId}
          docType="conflict_of_interest" label="Conflict of interest disclosure"
          hint="2 CFR 200.318(c)." />
        <DocumentUploader value={contractDocId}
          onChange={setContractDocId}
          docType="contract" label="Awarded contract"
          hint="Final executed contract PDF." />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={onCancel} style={{
          padding: '6px 14px', background: '#fff', color: '#0f172a',
          border: '1px solid var(--border, #e2e8f0)', borderRadius: 4,
          fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}>Cancel</button>
        <button onClick={handleSubmit} disabled={!eventCode || !description || !estimated || isSubmitting}
          style={{
            padding: '6px 14px',
            background: !eventCode || !description || !estimated || isSubmitting ? '#94a3b8' : '#0f172a',
            color: '#fff', border: 'none', borderRadius: 4,
            fontSize: 12, fontWeight: 600,
            cursor: !eventCode || !description || !estimated || isSubmitting ? 'not-allowed' : 'pointer',
          }}>{isSubmitting ? 'Saving…' : 'Save event'}</button>
      </div>
    </div>
  );
}


function EventDetail({ projectId, eventId, vendors, onClose }: { projectId: string; eventId: string; vendors: Vendor[]; onClose: () => void }) {
  const qc = useQueryClient();
  const base = `/bead-pm/projects/${projectId}/procurement-events/${eventId}`;
  const { data: lines = [] } = useQuery<any[]>({ queryKey: ['pe-lines', eventId], queryFn: () => api.get(`${base}/lines`) });
  const { data: analysis } = useQuery<any>({ queryKey: ['pe-analysis', eventId], queryFn: () => api.get(`${base}/cost-analysis`) });
  const { data: catalog = [] } = useQuery<any[]>({ queryKey: ['finance-catalog'], queryFn: () => api.get('/finance/catalog') });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['pe-lines', eventId] }); qc.invalidateQueries({ queryKey: ['pe-analysis', eventId] }); qc.invalidateQueries({ queryKey: ['procurement-events'] }); };
  const [ln, setLn] = useState<any>({ item_key: '', qty: '' });
  const addLine = useMutation({ mutationFn: () => api.post(`${base}/lines`, { item_key: ln.item_key, qty: Number(ln.qty) }), onSuccess: () => { setLn({ item_key: '', qty: '' }); refresh(); } });
  const delLine = useMutation({ mutationFn: (id: string) => api.del(`/bead-pm/procurement-event-lines/${id}`), onSuccess: refresh });
  const genIge = useMutation({ mutationFn: () => api.post(`${base}/compute-ige`, {}), onSuccess: refresh });
  const [qv, setQv] = useState<any>({ vendor_id: '', qnum: '', prices: {} });
  const addQuote = useMutation({ mutationFn: () => api.post(`${base}/quotes`, { vendor_id: qv.vendor_id, quote_number: qv.qnum || null, lines: (lines as any[]).filter((l: any) => Number(qv.prices[l.item_key]) > 0).map((l: any) => ({ item_key: l.item_key, unit_cost_usd: Number(qv.prices[l.item_key]) })) }), onSuccess: () => { setQv({ vendor_id: '', qnum: '', prices: {} }); refresh(); } });
  const usd = (n: any) => n == null ? '-' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const inp: any = { padding: '6px 8px', fontSize: 12, border: '1px solid var(--border,#e2e8f0)', borderRadius: 5, boxSizing: 'border-box' };
  const lbl: any = { fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', margin: '16px 0 6px' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 720, maxWidth: '100%', background: '#fff', height: '100vh', overflowY: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>Procurement event</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, fontSize: 24, cursor: 'pointer', color: '#64748b' }}>x</button>
        </div>

        <button onClick={() => window.open('/api/bead-pm/projects/' + projectId + '/procurement-events/' + eventId + '/packet', '_blank')} style={{ marginBottom: 14, padding: '8px 14px', background: '#0369a1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Generate RFP / audit packet (ZIP)</button>
        <div style={lbl}>Scope (what you are buying)</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead><tr style={{ background: '#f8fafc' }}>{['Item', 'Qty', 'Rate card $', 'Amount', ''].map((h, i) => <th key={h} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '6px 8px', fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>{h}</th>)}</tr></thead>
          <tbody>
            {(lines as any[]).map((l: any) => (
              <tr key={l.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={{ padding: '6px 8px' }}>{l.catalog_name || l.description || l.item_key}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(l.qty).toLocaleString()}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{usd(l.unit_cost_usd)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{usd(l.amount_usd)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}><button onClick={() => delLine.mutate(l.id)} style={{ background: 'transparent', border: 0, color: '#cbd5e1', cursor: 'pointer', fontSize: 14 }}>x</button></td>
              </tr>
            ))}
            {(lines as any[]).length === 0 && <tr><td colSpan={5} style={{ padding: 12, color: '#94a3b8', fontSize: 12 }}>No scope yet. Add the items this procurement is buying.</td></tr>}
          </tbody>
        </table>
        <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
          <select value={ln.item_key} onChange={e => setLn({ ...ln, item_key: e.target.value })} style={{ ...inp, flex: 2 }}><option value="">- add item -</option>{(catalog as any[]).map((c: any) => <option key={c.item_key} value={c.item_key}>{c.line_item}</option>)}</select>
          <input value={ln.qty} onChange={e => setLn({ ...ln, qty: e.target.value })} placeholder="qty" style={{ ...inp, width: 80, textAlign: 'right' }} />
          <button disabled={!ln.item_key || !ln.qty || addLine.isPending} onClick={() => addLine.mutate()} style={{ padding: '6px 12px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Add</button>
          <button disabled={!(lines as any[]).length || genIge.isPending} onClick={() => genIge.mutate()} title="Independent Cost Estimate = scope x rate card" style={{ padding: '6px 12px', background: '#0369a1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Generate IGE</button>
        </div>
        {analysis && <div style={{ marginTop: 8, fontSize: 13 }}>IGE (independent cost estimate): <b>{usd(analysis.ige_total)}</b> - bids on file: <b>{analysis.bid_count}</b></div>}

        <div style={lbl}>Add a vendor quote / bid</div>
        {(lines as any[]).length === 0 ? <div style={{ fontSize: 12, color: '#94a3b8' }}>Add scope items first, then enter each vendor price per item.</div> : (
          <div style={{ border: '1px solid var(--border,#e2e8f0)', borderRadius: 8, padding: 10 }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <select value={qv.vendor_id} onChange={e => setQv({ ...qv, vendor_id: e.target.value })} style={{ ...inp, flex: 2 }}><option value="">- vendor -</option>{vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select>
              <input value={qv.qnum} onChange={e => setQv({ ...qv, qnum: e.target.value })} placeholder="quote #" style={{ ...inp, flex: 1 }} />
            </div>
            {(lines as any[]).map((l: any) => (
              <div key={l.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <span style={{ flex: 1, fontSize: 12 }}>{l.catalog_name || l.item_key} <span style={{ color: '#94a3b8' }}>(IGE {usd(l.unit_cost_usd)})</span></span>
                <input value={qv.prices[l.item_key] ?? ''} onChange={e => setQv({ ...qv, prices: { ...qv.prices, [l.item_key]: e.target.value } })} placeholder="$/unit" style={{ ...inp, width: 110, textAlign: 'right' }} />
              </div>
            ))}
            <button disabled={!qv.vendor_id || addQuote.isPending} onClick={() => addQuote.mutate()} style={{ marginTop: 6, padding: '6px 12px', background: '#0f766e', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Record quote</button>
          </div>
        )}

        {analysis && analysis.vendor_totals && analysis.vendor_totals.length > 0 && (
          <div>
            <div style={lbl}>Cost / price analysis (2 CFR 200.324)</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead><tr style={{ background: '#f8fafc' }}>{['Vendor', 'Total', 'vs IGE', 'Items quoted'].map((h, i) => <th key={h} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '6px 8px', fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>{h}</th>)}</tr></thead>
              <tbody>
                {analysis.vendor_totals.map((v: any, i: number) => (
                  <tr key={v.vendor_id} style={{ borderTop: '1px solid #f1f5f9', background: i === 0 && !v.is_debarred ? '#f0fdf4' : undefined, opacity: v.is_debarred ? 0.55 : 1 }}>
                    <td style={{ padding: '6px 8px' }}>{v.vendor_name}{v.is_debarred ? <span style={{ marginLeft: 6, fontSize: 10, color: '#991b1b' }}>ineligible</span> : null}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{usd(v.total_usd)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: v.variance_vs_ige_pct > 0 ? '#dc2626' : '#16a34a' }}>{v.variance_vs_ige_pct == null ? '-' : (v.variance_vs_ige_pct > 0 ? '+' : '') + v.variance_vs_ige_pct + '%'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>{v.items_quoted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Generated from the scope, the IGE, and the captured quotes - your cost/price-analysis workpaper. Cheapest eligible vendor highlighted.</div>
          </div>
        )}
      </div>
    </div>
  );
}
